import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import ContactForm from "@/components/ContactForm";
import { Reveal, Eyebrow, RevealLines } from "@/components/Reveal";
import Seo from "@/components/Seo";

export default function Locazione() {
  const [properties, setProperties] = useState(null);

  useEffect(() => {
    api.get("/properties", { params: { status: "locazione" } })
      .then((r) => setProperties(r.data))
      .catch(() => setProperties([]));
  }, []);

  return (
    <div data-testid="locazione-page" className="pt-28 lg:pt-32">
      <Seo
        title="Immobili in affitto a Milano e in Lombardia | IDEAL"
        description="Case in affitto a Milano e in Lombardia: appartamenti arredati e soluzioni pronte da vivere, con canone, spese e condizioni contrattuali chiare."
        path="/locazione"
      />

      <header className="mx-auto max-w-[1440px] px-6 pb-16 lg:px-12">
        <Reveal><Eyebrow>Immobili in locazione</Eyebrow></Reveal>
        <h1>
          <RevealLines
            lines={["Locazioni a Milano", "e in Lombardia."]}
            className="mt-5 font-serif text-4xl font-medium leading-[1.05] lg:text-6xl"
          />
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-mut lg:text-lg">
            Appartamenti arredati e soluzioni pronte da vivere, con condizioni
            contrattuali chiare e l'assistenza del nostro team in ogni fase.
          </p>
        </Reveal>
      </header>

      <section className="mx-auto max-w-[1440px] px-6 py-10 lg:px-12 lg:py-16" data-testid="locazione-grid">
        {properties === null ? (
          <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] animate-pulse bg-sand3" />)}
          </div>
        ) : properties.length > 0 ? (
          <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {properties.map((p, i) => (
              <Reveal key={p.code} delay={(i % 3) * 0.08}>
                <PropertyCard property={p} />
              </Reveal>
            ))}
          </div>
        ) : (
          <p className="py-16 font-serif text-2xl italic text-mut">
            Nuove soluzioni in arrivo. Contattaci per essere aggiornato.
          </p>
        )}
      </section>

      {/* Proprietari locatori */}
      <section className="bg-sand2" data-testid="locazione-owners">
        <div className="mx-auto grid max-w-[1440px] gap-16 px-6 py-24 lg:grid-cols-2 lg:px-12 lg:py-32">
          <div>
            <Reveal><Eyebrow>Per i proprietari</Eyebrow></Reveal>
            <RevealLines
              lines={["Affida il tuo immobile", "a un professionista."]}
              className="mt-5 font-serif text-4xl font-medium leading-[1.05] lg:text-5xl"
            />
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ink2">
                Ti supportiamo nella ricerca di inquilini qualificati e nella gestione delle
                pratiche contrattuali: dalla stima del canone alla pubblicazione, dalle visite
                alla firma del contratto.
              </p>
            </Reveal>
            <div className="mt-10 space-y-5">
              {[
                "Valutazione del canone di mercato",
                "Selezione accurata degli inquilini",
                "Gestione completa delle pratiche contrattuali",
              ].map((item, i) => (
                <Reveal key={item} delay={0.2 + i * 0.08}>
                  <p className="flex items-start gap-4 text-[15px] font-light text-ink2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-champagne" />
                    {item}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.2}>
            <div className="border border-ink/10 bg-sand p-8 lg:p-12">
              <h3 className="font-serif text-3xl">Parliamone</h3>
              <p className="mt-2 text-sm font-light text-mut">Raccontaci il tuo immobile: ti ricontatteremo con una proposta su misura.</p>
              <div className="mt-8">
                <ContactForm variant="valutazione" reason="Locazione" testId="valuation-submit-button" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
